/**
 * Structured link values for CB2 props / journey params of type `link`.
 *
 * Legacy string URLs remain valid (opaque passthrough). New authoring stores a
 * discriminated `LinkValue` so internal pages can bind dynamic segments
 * (notably the shop trio) without baking a concrete URL into the journey.
 */

/** Segments that together identify a point-of-sale page. */
export const SHOP_PATH_SEGMENTS = ['shopCity', 'shopType', 'shopSlug'] as const;

export type ShopPathSegment = (typeof SHOP_PATH_SEGMENTS)[number];

/** Snapshot of a shop used to fill shop path segments at resolve time. */
export interface ShopRef {
  readonly shopId: string;
  readonly shopCity: string;
  readonly shopType: string;
  readonly shopSlug: string;
  /** Display name for studio summaries (optional, not required at runtime). */
  readonly shopName?: string;
}

export type LinkInputBinding =
  | { readonly source: 'activeShop' }
  | { readonly source: 'param'; readonly param: string }
  | { readonly source: 'text'; readonly value: string };

/** Scalar prop overrides applied when opening a modal from a link. */
export type ModalLinkProps = Readonly<Record<string, string | number | boolean | null>>;

export type LinkValue =
  | { readonly kind: 'external'; readonly url: string }
  | { readonly kind: 'mailto'; readonly email: string }
  | { readonly kind: 'tel'; readonly phone: string }
  | {
      readonly kind: 'page';
      /** Journey page key. */
      readonly page: string;
      /** Snapshot of the page URL template (e.g. `/:shopCity/:shopType/:shopSlug`). */
      readonly urlTemplate: string;
      readonly inputs?: Readonly<Record<string, LinkInputBinding>>;
    }
  | {
      readonly kind: 'modal';
      /** Runtime modal handle (journey key or DEFAULT_MODALS handle). */
      readonly handle: string;
      /** Where the handle was picked in the studio link assistant. */
      readonly source?: 'journey' | 'default';
      /** Component type used to resolve a ModalDefinition for prop overrides. */
      readonly type?: string;
      /** Scalar prop overrides passed to `ModalsService.open` as `data`. */
      readonly props?: ModalLinkProps;
    };

/** Payload for opening a modal from a structured link (no `modal://` URL). */
export interface ModalLinkOpen {
  readonly handle: string;
  readonly data: Readonly<Record<string, string | number | boolean | null>>;
}

export type LinkInputKind = 'shop' | 'text';

export interface LinkInputDefinition {
  readonly key: string;
  readonly kind: LinkInputKind;
  /** Path segments filled by this input (shop paths can use the shop route or ID). */
  readonly segments: readonly string[];
}

const SHOP_SEGMENT_SET = new Set<string>([...SHOP_PATH_SEGMENTS, 'shopId']);
const SHOP_ID_PATH_SEGMENTS = ['shopId', 'shop_id'] as const;

/** Path params allowed on pages offered in the CB2 link assistant. */
const STUDIO_LINKABLE_SEGMENTS = new Set<string>([
  ...SHOP_PATH_SEGMENTS,
  ...SHOP_ID_PATH_SEGMENTS,
  'categoryId'
]);

export function isShopRef(value: unknown): value is ShopRef {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj['shopId'] === 'string' &&
    typeof obj['shopCity'] === 'string' &&
    typeof obj['shopType'] === 'string' &&
    typeof obj['shopSlug'] === 'string'
  );
}

export function isLinkValue(value: unknown): value is LinkValue {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const kind = (value as { kind?: unknown }).kind;
  if (kind === 'external') {
    return typeof (value as { url?: unknown }).url === 'string';
  }
  if (kind === 'mailto') {
    return typeof (value as { email?: unknown }).email === 'string';
  }
  if (kind === 'tel') {
    return typeof (value as { phone?: unknown }).phone === 'string';
  }
  if (kind === 'page') {
    const page = value as { page?: unknown; urlTemplate?: unknown };
    return typeof page.page === 'string' && typeof page.urlTemplate === 'string';
  }
  if (kind === 'modal') {
    const modal = value as { handle?: unknown; props?: unknown };
    if (typeof modal.handle !== 'string' || !modal.handle.trim()) {
      return false;
    }
    if (modal.props != null) {
      if (typeof modal.props !== 'object' || Array.isArray(modal.props)) {
        return false;
      }
    }
    return true;
  }
  return false;
}

/** Extracts `:param` names from a path template. */
export function extractLinkPathParams(path: string): string[] {
  const params: string[] = [];
  const regex = /:([A-Za-z0-9_]+)/g;
  let match: RegExpExecArray | null = null;
  while ((match = regex.exec(path)) !== null) {
    const name = match[1];
    if (name) {
      params.push(name);
    }
  }
  return params;
}

export function pathHasShopSegment(path: string): boolean {
  return path.includes(':shopCity/:shopType/:shopSlug');
}

/**
 * Derives the authoring inputs required to complete a page URL.
 * Shop path params collapse into a single `shop` input; other segments become text.
 */
export function derivePageLinkInputs(urlTemplate: string): LinkInputDefinition[] {
  const params = extractLinkPathParams(urlTemplate);
  if (!params.length) {
    return [];
  }

  const inputs: LinkInputDefinition[] = [];
  const remaining = [...params];

  const hasShopPath = SHOP_PATH_SEGMENTS.every(segment => remaining.includes(segment));
  const shopIdSegments = SHOP_ID_PATH_SEGMENTS.filter(segment => remaining.includes(segment));
  const hasShopId = shopIdSegments.length > 0;
  if (hasShopPath || hasShopId) {
    const shopSegments = [
      ...(hasShopPath ? SHOP_PATH_SEGMENTS : []),
      ...shopIdSegments
    ];
    inputs.push({
      key: 'shop',
      kind: 'shop',
      segments: [...shopSegments],
    });
    for (const segment of shopSegments) {
      const index = remaining.indexOf(segment);
      if (index >= 0) {
        remaining.splice(index, 1);
      }
    }
  }

  for (const segment of remaining) {
    if (SHOP_SEGMENT_SET.has(segment)) {
      // Incomplete shop trio — fall back to individual text fields.
      inputs.push({ key: segment, kind: 'text', segments: [segment] });
      continue;
    }
    inputs.push({ key: segment, kind: 'text', segments: [segment] });
  }

  return inputs;
}

/**
 * Pages whose dynamic segments are only shop (+ optional categoryId) or none.
 * Hides order/product/check/payment routes from the studio link assistant.
 */
export function isStudioLinkablePageUrl(url: string): boolean {
  const params = extractLinkPathParams(url);
  return params.every(param => STUDIO_LINKABLE_SEGMENTS.has(param));
}

export function applyPathParamValues(
  urlTemplate: string,
  values: Readonly<Record<string, string>>,
): string {
  let result = urlTemplate.trim() || '/';
  for (const [key, value] of Object.entries(values)) {
    if (!value?.trim()) {
      continue;
    }
    result = result.replace(new RegExp(`:${key}\\b`, 'g'), value.trim());
  }
  if (!result.startsWith('/') && !result.startsWith('{{')) {
    result = `/${result}`;
  }
  return result.replace(/\/+/g, '/');
}

export interface ResolveLinkValueOptions {
  /** Resolves a journey param key to its stored value (ShopRef, string, …). */
  resolveParam?: (paramKey: string) => unknown;
  /** Resolves the active marketplace shop at runtime. */
  resolveActiveShop?: () => ShopRef | null;
}

/**
 * Turns a stored link (structured or legacy string) into a navigable URL string.
 * Unresolved dynamic segments leave the URL incomplete (empty string).
 */
export function resolveLinkValueToUrl(
  value: unknown,
  options: ResolveLinkValueOptions = {},
): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (!isLinkValue(value)) {
    return '';
  }

  switch (value.kind) {
    case 'external':
      return value.url.trim();
    case 'mailto': {
      const email = value.email.trim();
      if (!email) {
        return '';
      }
      return email.startsWith('mailto:') ? email : `mailto:${email}`;
    }
    case 'tel': {
      const phone = value.phone.trim();
      if (!phone) {
        return '';
      }
      return phone.startsWith('tel:') ? phone : `tel:${phone}`;
    }
    case 'page':
      return resolvePageLinkToUrl(value, options);
    case 'modal':
      // Modal links are not URLs — use resolveLinkValueToModalOpen / navigate(LinkValue).
      return '';
  }
}

/**
 * Extracts the open payload from a structured modal {@link LinkValue}.
 * Legacy `modal://` strings are not handled here (see ModalsService.openFromLink).
 */
export function resolveLinkValueToModalOpen(value: unknown): ModalLinkOpen | null {
  if (!isLinkValue(value) || value.kind !== 'modal') {
    return null;
  }
  const handle = value.handle.trim();
  if (!handle) {
    return null;
  }
  const data: Record<string, string | number | boolean | null> = {};
  if (value.props) {
    for (const [key, propValue] of Object.entries(value.props)) {
      if (propValue === undefined) {
        continue;
      }
      data[key] = propValue;
    }
  }
  return { handle, data };
}

function resolvePageLinkToUrl(
  link: Extract<LinkValue, { kind: 'page' }>,
  options: ResolveLinkValueOptions,
): string {
  const segmentValues: Record<string, string> = {};
  const inputs = link.inputs ?? {};
  const definitions = derivePageLinkInputs(link.urlTemplate);

  for (const definition of definitions) {
    const binding = inputs[definition.key];
    if (!binding) {
      continue;
    }

    if (definition.kind === 'shop') {
      const shop = resolveShopBinding(binding, options);
      if (!shop) {
        return '/';
      }
      for (const segment of definition.segments) {
        if (segment === 'shopId' || segment === 'shop_id') {
          segmentValues[segment] = shop.shopId;
        } else if (SHOP_SEGMENT_SET.has(segment)) {
          segmentValues[segment] = shop[segment as keyof ShopRef] as string;
        }
      }
      continue;
    }

    const text = resolveTextBinding(binding, options);
    if (!text) {
      return '';
    }
    for (const segment of definition.segments) {
      segmentValues[segment] = text;
    }
  }

  const resolved = applyPathParamValues(link.urlTemplate, segmentValues);
  if (extractLinkPathParams(resolved).length > 0) {
    return '';
  }
  return resolved;
}

function resolveShopBinding(
  binding: LinkInputBinding,
  options: ResolveLinkValueOptions,
): ShopRef | null {
  if (binding.source === 'activeShop') {
    return options.resolveActiveShop?.() ?? null;
  }
  if (binding.source === 'param') {
    const resolved = options.resolveParam?.(binding.param);
    return isShopRef(resolved) ? resolved : null;
  }
  return null;
}

function resolveTextBinding(
  binding: LinkInputBinding,
  options: ResolveLinkValueOptions,
): string {
  if (binding.source === 'text') {
    return binding.value.trim();
  }
  if (binding.source === 'param') {
    const resolved = options.resolveParam?.(binding.param);
    if (typeof resolved === 'string') {
      return resolved.trim();
    }
    if (isShopRef(resolved)) {
      return resolved.shopSlug;
    }
  }
  return '';
}

/**
 * Walk a prop tree and replace URL-like {@link LinkValue}s with strings.
 * Modal links stay structured so runtime can call `ModalsService.open`.
 */
export function resolveLinkValuesInTree(
  value: unknown,
  options: ResolveLinkValueOptions = {},
): unknown {
  if (typeof value === 'string') {
    return value;
  }
  if (isLinkValue(value)) {
    if (value.kind === 'modal') {
      return value;
    }
    return resolveLinkValueToUrl(value, options);
  }
  if (Array.isArray(value)) {
    return value.map(item => resolveLinkValuesInTree(item, options));
  }
  if (value != null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      out[key] = resolveLinkValuesInTree(child, options);
    }
    return out;
  }
  return value;
}

/** Human-readable empty check for studio validation. */
export function isLinkValueComplete(
  value: unknown,
  options: ResolveLinkValueOptions = {},
): boolean {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (!isLinkValue(value)) {
    return false;
  }
  if (value.kind === 'modal') {
    return value.handle.trim().length > 0;
  }
  return resolveLinkValueToUrl(value, options).length > 0;
}
